import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../../hooks/use-api";
import { useNotify } from "../../hooks/use-notify";
import { useSingleton } from "../../hooks/use-singleton";
import {
  apiStudentTestQuestionDetails,
  apiStudentAiSummary,
} from "../../utils/api-routes";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { user, board, medium, mail, role } = useSingleton();
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [boardData, setBoardData] = useState();
  const [showWeakness, setShowWeakness] = useState(false);
  const [weaknessDetails, setWeaknessDatails] = useState();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState({});
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [testBased, setTestBased] = useState("chapter");
  const [tiars, setTiars] = useState([
    { key: "top_tier", value: "TOP TIER" },
    { key: "low_tier", value: "LOW TIER" },
    { key: "mid_tier", value: "MID TIER" },
  ]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [moveTier, setMoveTier] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filterDatas, setFilterDatas] = useState({
    selectedExam: "",
    selectedClass: "",
    selectedTopic: "",
    selectedTieres: "",
    selectedChapter: "",
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const [performanceTable, setPerformanceTable] = useState();
  const [newbreakDetails, setNewBreakDetails] = useState();
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [testQuestionModalOpen, setTestQuestionModalOpen] = useState(false);
  const [testQuestionDetails, setTestQuestionDetails] = useState(null);
  const [testQuestionLoading, setTestQuestionLoading] = useState(false);
  const [selectedQuestionTest, setSelectedQuestionTest] = useState(null);

  const [studentAiModalOpen, setStudentAiModalOpen] = useState(false);
  const [studentAiModalData, setStudentAiModalData] = useState(null);
  const [studentAiModalLoading, setStudentAiModalLoading] = useState(false);
  const [selectedAiTest, setSelectedAiTest] = useState(null);

  const openStudentAiSummary = async (item) => {
    const roomCode = item?.room_code;
    const studentEmail =
      newbreakDetails?.summary?.email ||
      item?.student_email ||
      item?.email ||
      selectedStudent?.email ||
      "";

    setSelectedAiTest(item);
    setStudentAiModalOpen(true);
    setStudentAiModalData(null);

    if (!roomCode || !studentEmail) {
      setStudentAiModalData({ status: "empty" });
      return;
    }

    try {
      setStudentAiModalLoading(true);
      const url = apiStudentAiSummary(roomCode, studentEmail);
      const res = await get(url);
      const dataObj = res?.data || res || null;
      if (
        dataObj &&
        (dataObj.status === "success" || dataObj.ai_summary || dataObj.concept_data)
      ) {
        setStudentAiModalData(dataObj);
      } else {
        setStudentAiModalData(dataObj || { status: "empty" });
      }
    } catch (err) {
      console.error("Error fetching student AI summary:", err);
      setStudentAiModalData({
        status: "error",
        message:
          err?.response?.data?.detail ||
          "Failed to fetch AI summary for this test.",
      });
    } finally {
      setStudentAiModalLoading(false);
    }
  };

  const handleViewQuestionDetails = (item) => {
    setStudentAiModalOpen(false);
    getTestQuestionDetails(item || selectedAiTest);
  };

  const [examList, setExamList] = useState();
  const [classList, setClassList] = useState();
  const [chapterList, setChapterList] = useState();
  const [topicList, setTopicList] = useState();
  const [tierList, setTierList] = useState();

  const {
    selectedExam,
    selectedClass,
    selectedTopic,
    selectedTieres,
    selectedChapter,
  } = filterDatas;

  const course = user?.data?.course;
  const part = user?.data?.part;
  const paper = user?.data?.paper;
  const subject = user?.data?.subject;
  const section = user?.data?.section;
  const classs = user?.data?.clas;
  const exams = user?.data?.exam;

  const API_ENDPOINTS = {
    NEET: {
      leaderboard: `/teacher/exam/stats/users/leaderboard?subject=${subject}`,
      chapterBreakdown: (email) =>
        `/teacher/exam/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`,
      weaknessAnalysis: (email) =>
        `/student/weakness-analysis?user_mail=${email}&subject=${subject}`,
      sendTips: (email) =>
        `/teacher/exam/send/tips?user_mail=${email}&subject=${subject}`,
    },

    JEE: {
      leaderboard: `/teacher/exam/stats/users/leaderboard?subject=${subject}`,
      chapterBreakdown: (email) =>
        `/teacher/exam/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`,
      weaknessAnalysis: (email) =>
        `/student/weakness-analysis?user_mail=${email}&subject=${subject}`,
      sendTips: (email) =>
        `/teacher/exam/send/tips?user_mail=${email}&subject=${subject}`,
    },

    UPSC: {
      leaderboard: `/teacher/upsc/stats/users/leaderboard?paper=${paper}`,
      chapterBreakdown: (email) =>
        `/teacher/upsc/stats/aspirant/lesson-breakdown?user_mail=${email}&paper=${paper}`,
      weaknessAnalysis: (email) =>
        `/upsc/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&paper=${paper}`,
      sendTips: (email) =>
        `/teacher/upsc/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&paper=${paper}`,
    },

    TNPSC: {
      leaderboard: `/teacher/tnpsc/stats/users/leaderboard?part=${part}`,
      chapterBreakdown: (email) =>
        `/teacher/tnpsc/stats/aspirant/lesson-breakdown?user_mail=${email}&part=${part}`,
      weaknessAnalysis: (email) =>
        `/tnpsc/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&part=${part}`,
      sendTips: (email) =>
        `/teacher/tnpsc/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&part=${part}`,
    },

    Banking: {
      leaderboard: `/teacher/banking/stats/users/leaderboard?section=${section}`,
      chapterBreakdown: (email) =>
        `/teacher/banking/stats/student/chapter-breakdown?user_mail=${email}&section=${section}`,
      weaknessAnalysis: (email) =>
        `/banking/weakness-analysis?user_mail=${email}&section=${section}`,
      sendTips: (email) =>
        `/teacher/banking/send/tips?user_mail=${email}&section=${section}`,
    },

    CBSE: {
      leaderboard: `/teacher/student/stats/users/leaderboard?subject=${subject}`,
      chapterBreakdown: (email) =>
        `/teacher/student/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`,
      weaknessAnalysis: (email) =>
        `/scl/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`,
      sendTips: (email) =>
        `/teacher/student/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`,
    },

    TN_STATE_BOARD: {
      leaderboard: `/teacher/student/stats/users/leaderboard?subject=${subject}`,
      chapterBreakdown: (email) =>
        `/teacher/student/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`,
      weaknessAnalysis: (email) =>
        `/scl/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`,
      sendTips: (email) =>
        `/teacher/student/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`,
    },
  };

  // const NEET_JEE_LEADERBOARD = `/teacher/exam/stats/users/leaderboard?subject=${subject}`;
  // const UPSE_LEADERBOARD = `/teacher/upsc/stats/users/leaderboard?paper${paper}`;
  // const CBSE_TN_STATE_LEADERBOARD = `/teacher/student/stats/users/leaderboard?subject=${subject}`;
  // const TNPSC_LEADERBOARD = `/teacher/tnpsc/stats/users/leaderboard?part${part}`;
  // const BANKING_LEADERBOARD = `/teacher/banking/stats/users/leaderboard?section=${section}`;

  // const NEET_JEE_CHAPTER_BREAKDOM = `/teacher/exam/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`;
  // const UPSE_CHAPTER_BREAKDOM = `/teacher/upsc/stats/aspirant/lesson-breakdown?user_mail=${email}&paper=${paper}`;
  // const CBSE_TN_STATE_CHAPTER_BREAKDOM = `/teacher/student/stats/student/chapter-breakdown?user_mail=${email}&subject=${subject}`;
  // const TNPSC_CHAPTER_BREAKDOM = `/teacher/tnpsc/stats/aspirant/lesson-breakdown?user_mail=${email}&part=${part}`;
  // const BANKING_CHAPTER_BREAKDOM = `/teacher/banking/stats/student/chapter-breakdown?user_mail=${email}&section=${section}`;

  // const NEET_JEE_WEAKNESS_ANALYSIS = `/student/weakness-analysis?user_mail=${email}&subject=${subject}`;
  // const UPSE_WEAKNESS_ANALYSIS = `/upsc/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&paper=${paper}`;
  // const CBSE_TN_STATE_WEAKNESS_ANALYSIS = `/scl/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`;
  // const TNPSC_WEAKNESS_ANALYSIS = `/tnpsc/student/weakness-analysis?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&part=${part}`;
  // const BANKING_WEAKNESS_ANALYSIS = `banking/weakness-analysis?user_mail=${email}&section=${section}`;

  // const NEET_JEE_SEND_TIPS = `/teacher/exam/send/tips?user_mail=${email}&subject=${subject}`;
  // const UPSE_SEND_TIPS = `/teacher/upsc/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&paper=${paper}`;
  // const CBSE_TN_STATE_SEND_TIPS = `/teacher/student/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&subject=${subject}`;
  // const TNPSC_SEND_TIPS = `/teacher/tnpsc/send/tips?user_mail=${email}&board=${board}&clas=${classs}&medium=${medium}&part=${part}`;
  // const BANKING_SEND_TIPS = `/teacher/banking/send/tips?user_mail=${email}&section=${section}`;

  const api = API_ENDPOINTS[course];

  const ITEMS_PER_PAGE = 5;

  const totalPages = Math.ceil(performanceTable?.length / ITEMS_PER_PAGE);

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const endItem = Math.min(
    currentPage * ITEMS_PER_PAGE,
    performanceTable?.length,
  );

  const currentStudents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return performanceTable?.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, performanceTable]);

  const getStudentLeaderBoardDetails = async () => {
    try {
      const res = await get(api.leaderboard);
      if (res) {
        setBoardData(res?.data || []);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET TEACHER STUNDETS LAEDERBOARD", e);
    }
  };

  const getStudentLeaderBoardChapterBreakDown = async (email) => {
    try {
      const res = await get(api.chapterBreakdown(email));
      if (res) {
        setSelectedStudent(res?.data || {});
        setOpenPopup(true);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET TEACHER STUNDETS LAEDERBOARD", e);
    }
  };

  const getWeakneesAnalysis = async (email) => {
    try {
      setLoading(true);
      // old
      // const res = await get(api.weaknessAnalysis(email));
      // new
      const res = await get(
        `student/weakness-analysis?user_mail=${email}&subject=${subject}`,
      );

      if (res) {
        setWeaknessDatails(res?.data);
        setShowWeakness(true);
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      console.log("ERROR COMES FROM WEAKNESS ANALYSIS API", e);
    }
  };

  const teacherSendTipsToStudents = async (email) => {
    try {
      setLoading(true);
      const res = await post(api.sendTips(email));
      if (res) {
        toggleNotify({
          type: "success",
          title: "Success",
          description: res?.data?.message,
        });
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
      console.log("ERROR COMES FROM TEACHER SEND TIPS API", e);
    }
  };

  useEffect(() => {
    getStudentLeaderBoardDetails();
  }, []);

  const handleTierChange = (email, value) => {
    setSelectedTier((prev) => ({
      ...prev,
      [email]: value,
    }));
  };

  const handleSelectStudent = (email) => {
    setSelectedStudents((prev) =>
      prev.includes(email)
        ? prev.filter((item) => item !== email)
        : [...prev, email],
    );
  };

  const onMoveTier = (tier) => {
    setMoveTier(tier);
    setShowConfirm(true);
  };

  const clearFilters = () =>
    setFilterDatas({
      selectedExam: "",
      selectedClass: "",
      selectedTopic: "",
      selectedTieres: "",
      selectedChapter: "",
    });

  const onChangeValue = (e) => {
    const { name, value } = e.target;
    setFilterDatas((prev) => ({ ...prev, [name]: value }));
  };

  const onConfirmMoveTier = () => {
    handleTierChange(selectedStudent, moveTier);

    // API call here later

    setShowConfirm(false);
  };

  useEffect(() => {
    const handleClickOut = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOut);

    return () => {
      document.removeEventListener("mousedown", handleClickOut);
    };
  }, []);

  const getAllExams = async () => {
    if (!mail || !role) return;
    try {
      const res = await get(`teacher/test/exams?email=${mail}&role=${role}`);
      if (res) {
        setExamList(res?.data?.exams);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET ALL EXAMS", e);
    }
  };

  const getAllClass = async (selectexam) => {
    try {
      const res = await get(
        `teacher/test/classes?exam=${selectexam}&email=${mail}&role=${role}`,
      );
      if (res) {
        setClassList(res?.data?.classes);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET ALL CLASSS", e);
    }
  };

  const getAllChapters = async (selectexam, selectclass = "") => {
    try {
      let url = `teacher/test/chapters?exam=${selectexam}`;
      if (selectclass) url += `&class=${encodeURIComponent(selectclass)}`;
      url += `&email=${mail}&role=${role}`;
      const res = await get(url);
      if (res) {
        setChapterList(res?.data?.chapters);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET ALL CLASSS", e);
    }
  };
  const getAllTopics = async (selectexam, selectchapter) => {
    try {
      const res = await get(
        `teacher/test/sub-topics?exam=${selectexam}&chapter=${selectchapter}&email=${mail}&role=${role}`,
      );
      if (res) {
        setTopicList(res?.data?.sub_topics);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET ALL TOPICS", e);
    }
  };
  const getAllStudentTiers = async () => {
    try {
      const res = await get("api/teacher/student-performance/tiers");
      if (res) {
        const { tiers } = res?.data;
        setTierList(tiers);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET ALL STUDENT TIERS", e);
    }
  };

  const getStudentPerformanceTable = async () => {
    if (!mail) return;
    try {
      const res = await get(`api/teacher/student-performance?mail=${mail}`);
      if (res) {
        setPerformanceTable(res?.data || []);
      }
    } catch (e) {
      console.log("ERROR COMES FROM STUDENT PERFORMANCE TABLE", e);
    }
  };

  const applyFilters = async () => {
    const tierSelect =
      selectedTieres === "Top tier"
        ? "top_tier"
        : selectedTieres === "Mid tier"
          ? "mid_tier"
          : selectedTieres === "All"
            ? "All"
            : selectedTieres === "Low Tier"
              ? "low_tier"
              : "";
    if (
      !selectedChapter ||
      !selectedClass ||
      !selectedExam ||
      !selectedTieres ||
      !selectedTopic
    ) {
      toggleNotify({
        type: "error",
        title: "Error",
        description: "Please Choose chapter,class,exam,topic and tier",
      });
      return;
    }

    try {
      const res = await get(
        `api/teacher/student-performance?mail=${mail}&exam=${selectedExam}&clas=${selectedClass}&subject=${subject}&chapter=${selectedChapter}&topic=${selectedTopic}&student_tier=${tierSelect}`,
      );
      if (res) {
        setShowFilter(false);
        setPerformanceTable(res?.data || []);
        clearFilters();
      }
    } catch (e) {
      console.log("ERROR COMES FROM APPLY FILTER", e);
    }
  };

  const changeStudentTiers = async () => {
    if (!moveTier || !selectedStudents.length) return;
    const payload = {
      emails: selectedStudents,
      target_tier: moveTier,
    };
    try {
      const res = await post("api/teacher/students/change-tier", payload);
      if (res) {
        toggleNotify({
          type: "success",
          title: "Success",
          description: res?.data?.message,
        });
        setSelectedTier((prev) => {
          const updated = { ...prev };
          selectedStudents.forEach((email) => {
            updated[email] = moveTier;
          });
          return updated;
        });
        getStudentPerformanceTable();
        setSelectedStudents([]);
      }
    } catch (e) {
      console.log("ERROR COMES FROM CHANGE STUDENT TIER", e);
    }
  };

  const getNewChapterBreakDown = async (email, chapter) => {
    const studentEmail = email || newbreakDetails?.summary?.email;
    if (!studentEmail) return;
    try {
      setBreakdownLoading(true);
      const targetExam = filterDatas?.selectedExam || exams || course || "NEET, JEE";
      const targetSubject = subject || "Chemistry";
      const viewType = chapter || testBased || "chapter";
      const res = await get(
        `api/teacher/student/${encodeURIComponent(studentEmail)}/weakness-details?view_type=${encodeURIComponent(viewType)}&subject=${encodeURIComponent(targetSubject)}&exam=${encodeURIComponent(targetExam)}`,
      );
      if (res) {
        //  setSelectedStudent(res?.data || {});
        setNewBreakDetails(res?.data || {});
        setOpenPopup(true);
      }
    } catch (e) {
      console.log("ERROR COMES FROM GET TEACHER STUNDETS LAEDERBOARD", e);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const getTestQuestionDetails = async (item) => {
    const testName = item?.test_name || item?.chapter_name || item?.chapter || "";
    const studentEmail =
      newbreakDetails?.summary?.email ||
      item?.student_email ||
      item?.email ||
      selectedStudent?.email ||
      "";
    const roomCode = item?.room_code || "";

    if (!testName || !studentEmail) {
      toggleNotify({
        type: "error",
        title: "Missing Information",
        description: "Test name or student email is missing.",
      });
      return;
    }

    try {
      setSelectedQuestionTest(item);
      setTestQuestionLoading(true);

      const url = apiStudentTestQuestionDetails(testName, studentEmail, roomCode);
      const res = await get(url);
      const dataObj = res?.data || res || {};

      if (dataObj?.status === "error" || (dataObj?.status && dataObj.status !== "success")) {
        setTestQuestionModalOpen(false);
        setTestQuestionDetails(null);
        toggleNotify({
          type: "error",
          title: "Error",
          description: dataObj?.message || dataObj?.detail || "Failed to fetch question details for this test.",
        });
        return;
      }

      setTestQuestionDetails(dataObj);
      setTestQuestionModalOpen(true);
    } catch (err) {
      console.error("Error fetching test question details", err);
      setTestQuestionModalOpen(false);
      setTestQuestionDetails(null);
      toggleNotify({
        type: "error",
        title: "Error",
        description: err?.response?.data?.detail || "Failed to fetch question details for this test.",
      });
    } finally {
      setTestQuestionLoading(false);
    }
  };

  useEffect(() => {
    getStudentPerformanceTable();
  }, []);

  useEffect(() => {
    getAllExams();
  }, []);

  useEffect(() => {
    getAllStudentTiers();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      getAllClass(selectedExam);
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedExam && selectedClass) {
      getAllChapters(selectedExam, selectedClass);
    } else {
      setChapterList([]);
    }
  }, [selectedExam, selectedClass]);

  useEffect(() => {
    if ((selectedExam, selectedChapter)) {
      getAllTopics(selectedExam, selectedChapter);
    }
  }, [selectedExam, selectedChapter]);

  useEffect(() => {
    if (newbreakDetails?.summary?.email) {
      getNewChapterBreakDown(newbreakDetails?.summary?.email, testBased);
    }
  }, [testBased]);

  return {
    openPopup,
    selectedStudent,
    totalPages,
    currentPage,
    startItem,
    endItem,
    currentStudents,
    boardData,
    weaknessDetails,
    showWeakness,
    loading,
    user,
    tiars,
    selectedTier,
    selectedStudents,
    testBased,
    showConfirm,
    moveTier,
    showFilter,
    filterDatas,
    openDropdown,
    dropdownRef,
    examList,
    classList,
    topicList,
    chapterList,
    tierList,
    performanceTable,
    newbreakDetails,
    breakdownLoading,
    setWeaknessDatails,
    setShowWeakness,
    setOpenPopup,
    setSelectedStudent,
    setCurrentPage,
    getStudentLeaderBoardChapterBreakDown,
    getWeakneesAnalysis,
    teacherSendTipsToStudents,
    setTiars,
    setSelectedTier,
    getStudentLeaderBoardDetails,
    setLoading,
    handleTierChange,
    handleSelectStudent,
    setSelectedStudents,
    setTestBased,
    setShowConfirm,
    setMoveTier,
    onMoveTier,
    setShowFilter,
    clearFilters,
    onChangeValue,
    setOpenDropdown,
    applyFilters,
    changeStudentTiers,
    getNewChapterBreakDown,
    getStudentPerformanceTable,
    testQuestionModalOpen,
    setTestQuestionModalOpen,
    testQuestionDetails,
    testQuestionLoading,
    selectedQuestionTest,
    studentAiModalOpen,
    setStudentAiModalOpen,
    studentAiModalData,
    studentAiModalLoading,
    selectedAiTest,
    openStudentAiSummary,
    handleViewQuestionDetails,
  };
};

export default useHandler;
